(() => {
  if (customElements.get('pclan-mega-menu')) return;
  customElements.define('pclan-mega-menu', class extends HTMLElement {
    connectedCallback() {
      this.initialFrame = requestAnimationFrame(() => this.init());
    }
    init() {
      this.disclosure = this.querySelector('.pc-mm__disclosure');
      this.trigger = this.querySelector('.pc-mm__trigger');
      this.panel = this.querySelector('.pc-mm__panel');
      this.categories = Array.from(this.querySelectorAll('[data-category]'));
      this.contents = Array.from(this.querySelectorAll('[data-content]'));
      this.banners = Array.from(this.querySelectorAll('[data-banner]'));
      if (!this.disclosure || !this.panel || !this.categories.length) return;
      this.controller?.abort();
      this.controller = new AbortController();
      const signal = this.controller.signal;
      const on = (node, event, callback, extra = {}) => node.addEventListener(event, callback, { signal, ...extra });
      this.setupProducts(on);
      let backdrop = this.querySelector('.pc-mm__backdrop');
      if (!backdrop) {
        backdrop = document.createElement('div');
        backdrop.className = 'pc-mm__backdrop';
        backdrop.setAttribute('aria-hidden', 'true');
        this.prepend(backdrop);
      }
      on(backdrop, 'pointerdown', event => {
        event.preventDefault();
        event.stopPropagation();
        this.close(true);
      });
      on(backdrop, 'click', event => {
        event.preventDefault();
        event.stopPropagation();
      });
      this.desktop = matchMedia('(min-width: 1000px)');
      this.hover = matchMedia('(hover: hover) and (pointer: fine)');
      this.defaultKey = this.categories.some(b => b.dataset.category === this.dataset.default)
        ? this.dataset.default : this.categories[0].dataset.category;
      this.select(this.defaultKey, true);
      this.setAttribute('data-ready', '');
      this.trigger.setAttribute('aria-expanded', String(this.disclosure.open));
      on(this.disclosure, 'toggle', () => {
        this.trigger.setAttribute('aria-expanded', String(this.disclosure.open));
        if (this.disclosure.open) {
          if (!this.desktop.matches) { this.close(); return; }
          this.closeOtherMenus();
          this.select(this.defaultKey, true);
          this.position();
          // Load each configured banner once when the menu is first used.
          // Hidden lazy images otherwise start only after the pointer reaches a category.
          this.banners.forEach(banner => this.prepareImage(banner));
        } else {
          this.cancelProducts();
          this.revision = (this.revision || 0) + 1;
          clearTimeout(this.closeTimer);
        }
      });
      on(this, 'pointerenter', () => {
        clearTimeout(this.closeTimer);
        if (this.dataset.openTrigger === 'hover' && this.hover.matches) this.open();
      });
      on(this, 'pointerleave', () => {
        if (this.hover.matches) this.closeTimer = setTimeout(() => {
          this.dismissAutomatically();
        }, 260);
      });
      this.categories.forEach((button, index) => {
        on(button, 'pointerenter', () => {
          if (this.hover.matches) this.select(button.dataset.category);
        });
        on(button, 'focus', () => this.select(button.dataset.category));
        on(button, 'click', () => this.select(button.dataset.category));
        on(button, 'keydown', event => {
          let next;
          if (event.key === 'ArrowDown') next = (index + 1) % this.categories.length;
          if (event.key === 'ArrowUp') next = (index - 1 + this.categories.length) % this.categories.length;
          if (event.key === 'Home') next = 0;
          if (event.key === 'End') next = this.categories.length - 1;
          if (next !== undefined) {
            event.preventDefault();
            this.categories[next].focus();
            this.categories[next].scrollIntoView({ block: 'nearest' });
          }
          if (event.key === 'ArrowRight') {
            event.preventDefault();
            this.contents.find(c => c.dataset.content === this.activeKey)?.querySelector('a')?.focus();
          }
        });
      });
      on(this.trigger, 'keydown', event => {
        if (event.key === 'ArrowDown') {
          event.preventDefault();
          this.open();
          this.categories.find(b => b.dataset.category === this.defaultKey)?.focus();
        }
      });
      on(this.querySelector('.pc-mm__middle'), 'keydown', event => {
        if (event.key === 'ArrowLeft') {
          event.preventDefault();
          this.categories.find(b => b.dataset.category === this.activeKey)?.focus();
        }
      });
      on(this.querySelector('.pc-mm__close'), 'click', () => this.close(true));
      on(document, 'pointerdown', event => {
        if (this.disclosure.open && !this.contains(event.target)) this.close();
      });
      on(document, 'keydown', event => {
        if (event.key === 'Escape' && this.disclosure.open && !this.quickViewActive) {
          event.preventDefault();
          this.close(true);
        }
      });
      on(this, 'focusout', () => queueMicrotask(() => {
        if (this.isConnected) this.dismissAutomatically();
      }));
      on(window, 'resize', () => this.schedulePosition(), { passive: true });
      on(window, 'scroll', () => this.schedulePosition(), { passive: true });
      on(this.desktop, 'change', () => { if (!this.desktop.matches) this.close(); });
      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(() => this.schedulePosition());
        this.resizeObserver.observe(this.closest('[data-section-type="header"]') || this);
      }
      const quickViewModal = document.getElementById(this.dataset.quickViewModal);
      if (quickViewModal) on(quickViewModal, 'modal:closed', () => {
        this.finishQuickView();
      });
      on(document, 'shopify:block:select', event => {
        if (event.target === this.closest('.pc-mm-nav-item')) { this.open(); return; }
        const banner = event.target.closest?.('.pc-mm__banner');
        if (!banner || !this.contains(banner)) return;
        this.open();
        requestAnimationFrame(() => this.select(banner.dataset.banner, true));
      });
    }
    closeOtherMenus() {
      document.querySelectorAll('pclan-mega-menu, pclan-top-dropdown').forEach(menu => {
        if (menu === this) return;
        const disclosure = menu.querySelector('details');
        if (disclosure?.open) {
          if (typeof menu.close === 'function') menu.close();
          else disclosure.open = false;
        }
      });
    }
    open() {
      if (this.quickViewActive) return;
      if (!this.desktop.matches) return;
      clearTimeout(this.closeTimer);
      if (this.disclosure.open) { this.position(); return; }
      this.closeOtherMenus();
      this.disclosure.open = true;
      this.position();
    }
    close(restoreFocus = false) {
      if (this.quickViewActive && this.desktop.matches) return;
      this.keepOpenAfterQuickView = false;
      this.cancelProducts();
      clearTimeout(this.closeTimer);
      if (this.disclosure) this.disclosure.open = false;
      this.revision = (this.revision || 0) + 1;
      if (restoreFocus) this.trigger?.focus();
    }
    select(key, immediate = false) {
      if (this.quickViewActive) return;
      const categoryURL = this.categories.find(button => button.dataset.category === key)?.dataset.collectionUrl;
      const content = this.contents.find(item => item.dataset.content === key);
      const collection = this.previewURL(categoryURL)
        || ((!categoryURL || categoryURL.trim() === '#') ? this.firstSubmenuCollection(content) : null);
      this.queueProducts(collection?.href, immediate);
      if (!immediate && this.activeKey === key) return;
      this.activeKey = key;
      this.categories.forEach(button => button.setAttribute('aria-expanded', String(button.dataset.category === key)));
      this.contents.forEach(content => { content.hidden = content.dataset.content !== key; });
      const middle = this.querySelector('.pc-mm__middle');
      const activeContent = this.contents.find(content => content.dataset.content === key);
      const hideMiddle = Boolean(this.productPanel && !activeContent?.querySelector('.pc-mm__group'));
      this.classList.toggle('pc-mm--empty-middle', hideMiddle);
      if (middle) {
        middle.hidden = hideMiddle;
        middle.scrollTop = 0;
      }
      const next = this.banners.find(banner => banner.dataset.banner === key);
      const revision = this.revision = (this.revision || 0) + 1;
      const show = () => {
        if (!this.isConnected || revision !== this.revision) return;
        this.banners.forEach(banner => {
          banner.hidden = banner !== next;
          banner.classList.remove('is-arriving');
        });
        if (!immediate) next?.classList.add('is-arriving');
      };
      // Keep the existing banner visible until the new image is decoded.
      // A revision token prevents a late image from overwriting a newer hover.
      if (immediate || !next?.querySelector('img')) show();
      else this.prepareImage(next).then(show);
    }
    dismissAutomatically() {
      // The product grid can replace the focused Quick view button while hovering.
      // Focus loss and popup pointer transitions must not dismiss a restored menu.
      if (this.quickViewActive || this.keepOpenAfterQuickView) return;
      if (this.contains(document.activeElement) || this.matches(':hover')) return;
      this.close();
    }
    finishQuickView() {
      if (!this.quickViewActive) return;
      this.quickViewActive = false;
      this.keepOpenAfterQuickView = true;
      clearTimeout(this.closeTimer);
      const button = this.quickViewReturn;
      this.quickViewReturn = null;
      if (!this.isConnected) return;
      if (!this.desktop.matches) { this.close(); return; }
      if (this.disclosure.open) {
        const target = button?.isConnected ? button : this.trigger;
        target.focus({ preventScroll: true });
        (this.quickViewScroll || []).forEach(([node, top]) => { if (node.isConnected) node.scrollTop = top; });
        this.position();
      }
      this.quickViewScroll = null;
    }
    setupProducts(on) {
      this.productPanel = this.querySelector('[data-pc-products]');
      this.productCache = new Map();
      this.productVersion = 0;
      if (!this.productPanel) return;
      const middle = this.querySelector('.pc-mm__middle');
      const pick = event => {
        const link = event.target.closest('a[href]');
        if (link && middle.contains(link)) {
          const href = link.getAttribute('href');
          const collection = this.previewURL(href)
            || ((!href || href.trim() === '#') ? this.firstSubmenuCollection(link.closest('.pc-mm__group')) : null);
          this.queueProducts(collection?.href);
        }
      };
      on(middle, 'pointerover', pick);
      on(middle, 'focusin', pick);
      on(this.productPanel, 'click', event => {
        const button = event.target.closest('[data-pc-menu-quick-view]');
        if (!button) return;
        event.preventDefault();
        const launcher = document.getElementById(this.dataset.quickViewLauncher);
        if (!launcher) return;
        launcher.setAttribute('data-product-url', button.dataset.productUrl);
        this.quickViewReturn = button;
        this.quickViewActive = true;
        clearTimeout(this.closeTimer);
        this.cancelProducts();
        this.quickViewScroll = [this.productPanel, this.querySelector('.pc-mm__middle'), this.querySelector('.pc-mm__categories')]
          .filter(Boolean).map(node => [node, node.scrollTop]);
        launcher.click();
        // If the theme did not open its popup, do not leave menu dismissal suspended.
        queueMicrotask(() => {
          const modal = document.getElementById(this.dataset.quickViewModal);
          if (!modal || modal.getAttribute('aria-hidden') !== 'false') this.finishQuickView();
        });
      });
    }
    firstSubmenuCollection(content) {
      if (!content) return null;
      for (const link of content.querySelectorAll('.pc-mm__group a[href], a[href]')) {
        // Skip the hidden root category heading; only consider submenu links.
        if (link.closest('.pc-mm__category-heading')) continue;
        const collection = this.previewURL(link.getAttribute('href'));
        if (collection) return collection;
      }
      return null;
    }
    previewURL(value) {
      if (!value || value === '#') return null;
      try {
        const url = new URL(value, location.origin);
        if (url.origin !== location.origin || !/\/(?:collections\/[^/]+(?:\/products\/[^/]+)?|products\/[^/]+)\/?$/.test(url.pathname)) return null;
        url.hash = '';
        url.searchParams.delete('page');
        url.searchParams.delete('section_id');
        return url;
      } catch { return null; }
    }
    cancelProducts() {
      clearTimeout(this.productTimer);
      this.productAbort?.abort();
      this.productVersion = (this.productVersion || 0) + 1;
      this.productRequested = null;
      this.productPanel?.removeAttribute('aria-busy');
    }
    queueProducts(value, immediate = false) {
      if (this.quickViewActive) return;
      if (!this.productPanel || !this.disclosure.open) return;
      const url = this.previewURL(value);
      this.classList.toggle('pc-mm--hide-products', !url);
      this.productPanel.hidden = !url;
      if (!url) {
        this.cancelProducts();
        const status = this.productPanel.querySelector('[data-pc-product-status]');
        this.productShown = null;
        this.productPanel.querySelector('[data-pc-product-results]')?.replaceChildren();
        if (status) status.textContent = '';
        return;
      }
      const key = url.href;
      if (key === this.productRequested || key === this.productShown) {
        if (key === this.productShown && this.productRequested !== key) {
          this.cancelProducts();
          this.productPanel.querySelector('[data-pc-product-status]').textContent = '';
        }
        return;
      }
      this.cancelProducts();
      this.productShown = null;
      this.productPanel.querySelector('[data-pc-product-results]')?.replaceChildren();
      this.productPanel.querySelector('[data-pc-product-status]').textContent = '';
      this.productRequested = key;
      const version = this.productVersion;
      this.productTimer = setTimeout(() => this.loadProducts(url, key, version), immediate ? 0 : 140);
    }
    async loadProducts(url, key, version) {
      const status = this.productPanel.querySelector('[data-pc-product-status]');
      const results = this.productPanel.querySelector('[data-pc-product-results]');
      if (version !== this.productVersion || !this.isConnected || !this.disclosure.open) return;
      this.productPanel.setAttribute('aria-busy', 'true');
      status.textContent = '';
      const abort = this.productAbort = new AbortController();
      try {
        let markup = this.productCache.get(key);
        if (!markup) {
          const endpoint = new URL(url.href);
          endpoint.searchParams.set('section_id', 'pclan-menu-products');
          const response = await fetch(endpoint.href, { signal: abort.signal, credentials: 'same-origin' });
          if (!response.ok) throw new Error('Collection response failed');
          markup = await response.text();
        }
        if (abort.signal.aborted || version !== this.productVersion || !this.isConnected || !this.disclosure.open) return;
        const doc = new DOMParser().parseFromString(markup, 'text/html');
        const content = doc.querySelector('[data-pc-menu-products-response]');
        if (!content || !content.querySelector('.pc-mm-products__heading, .pc-mm-product')) throw new Error('Collection section unavailable');
        content.querySelectorAll('script').forEach(script => script.remove());
        const count = Math.max(1, Math.min(10, Number(this.dataset.productCount) || 10));
        content.querySelectorAll('.pc-mm-product').forEach((card, index) => { if (index >= count) card.remove(); });
        content.querySelectorAll('[data-pc-menu-quick-view]').forEach(button => button.setAttribute('aria-controls', this.dataset.quickViewModal));
        results.replaceChildren(...Array.from(content.childNodes));
        this.productPanel.scrollTop = 0;
        this.productShown = key;
        this.productCache.set(key, markup);
        if (this.productCache.size > 20) this.productCache.delete(this.productCache.keys().next().value);
        status.textContent = '';
      } catch (error) {
        if (abort.signal.aborted || version !== this.productVersion) return;
        status.replaceChildren(document.createTextNode('Products could not load. '));
        const link = document.createElement('a');
        link.href = url.href; link.textContent = /\/products\//.test(url.pathname) ? 'View product' : 'View collection';
        status.append(link);
      } finally {
        if (version === this.productVersion) {
          this.productRequested = null;
          this.productPanel.removeAttribute('aria-busy');
        }
      }
    }

    prepareImage(banner) {
      const image = banner.querySelector('img');
      if (!image) return Promise.resolve();
      image.loading = 'eager';
      if (image.decode) return image.decode().catch(() => {});
      return Promise.resolve();
    }
    schedulePosition() {
      if (!this.disclosure?.open || this.positionFrame) return;
      this.positionFrame = requestAnimationFrame(() => {
        this.positionFrame = 0;
        this.position();
      });
    }
    position() {
      if (this.quickViewActive) return;
      const rect = this.trigger.getBoundingClientRect();
      if (rect.bottom <= 0 || rect.top >= window.innerHeight) { this.close(); return; }
      const navigation = this.closest('.nav-bar');
      const anchor = navigation || this.closest('[data-section-type="header"]');
      const anchorRect = anchor ? anchor.getBoundingClientRect() : rect;
      const container = navigation?.querySelector('.container');
      // Use the navigation content edge, excluding its outer container padding.
      // All left-aligned mega menus share this anchor.
      const navigationList = navigation?.querySelector('.nav-bar__linklist');
      const left = navigationList
        ? navigationList.getBoundingClientRect().left
        : container
          ? container.getBoundingClientRect().left + (parseFloat(getComputedStyle(container).paddingLeft) || 0)
          : rect.left;
      const gap = Math.max(0, Math.min(100, Number(this.dataset.topGap) || 0));
      const top = Math.max(0, Math.ceil(Math.max(rect.bottom, anchorRect.bottom))) + gap;
      const width = Math.min(parseFloat(getComputedStyle(this).getPropertyValue('--pc-mm-width')) || 1320, window.innerWidth - 32);
      this.style.setProperty('--pc-mm-top', `${top}px`);
      const desiredLeft = this.dataset.position === 'center' ? (window.innerWidth - width) / 2 : left;
      this.style.setProperty('--pc-mm-left', `${Math.max(16, Math.min(desiredLeft, window.innerWidth - width - 16))}px`);
      this.style.setProperty('--pc-mm-available', `${Math.max(0, window.innerHeight - top - 16)}px`);
    }
    disconnectedCallback() {
      this.cancelProducts();
      cancelAnimationFrame(this.initialFrame);
      cancelAnimationFrame(this.positionFrame);
      clearTimeout(this.closeTimer);
      this.controller?.abort();
      this.resizeObserver?.disconnect();
      this.revision = (this.revision || 0) + 1;
      this.positionFrame = 0;
    }
  });
})();
